import { useState, useMemo, useCallback, type ComponentType } from 'react';
import { useParams } from '@tanstack/react-router';
import { Plus, Users, Settings, Puzzle, Gauge, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { useListTenants } from '@/pages/admin/api/queries/useTenants';
import { useListTenantUsers } from '@/pages/admin/api/queries/useTenantUsers';
import {
  useSendTenantUserInvitation,
  useGenerateTenantUserActivationLink,
  useDeleteTenantUser,
  usePendingTenantUserIds
} from '@/pages/admin/api/mutations/useTenantUsers';
import {
  Button,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Page,
  PageContent,
  DeleteConfirmDialog,
  DataTable,
  createActionsColumn,
  createColumnHeader,
  filterArrayIncludes,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { HomeOrgDeletedBadge } from '@/components/home-org-deleted-badge/HomeOrgDeletedBadge';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { BackButton } from '@/components/back-button';
import { AddTenantUserDialog } from './AddTenantUserDialog';
import { TenantSettingsTab } from './components/TenantSettingsTab';
import { TenantTypeBadge } from './components/TenantTypeBadge';
import { TenantAppsTab } from './components/TenantAppsTab';
import { TenantAiUsageTab } from './components/TenantAiUsageTab';
import { ResetTenantUserSignInDialog } from './components/ResetTenantUserSignInDialog';
import { getTenantUserStatusActions } from './components/getTenantUserStatusActions';
import {
  runsCustomerApp,
  type TenantSummary,
  type TenantUserAdminListItem,
  type UserType,
  type UserStatus
} from '@bliks-io/contracts';
import {
  getUserStatusColorScheme,
  getUserStatusLabel,
  getUserClassificationLabel,
  getUserClassificationOptions,
  getUserStatusOptions,
  getUserTypeOptions
} from '@/lib/user-utils';

/** What every tab panel is handed — a panel is free to read only part of it. */
interface TenantTabPanelProps {
  tenantId: string;
  tenant: TenantSummary;
}

/**
 * The tabs a customer-app tenant gets on top of Users. Declared once so the
 * trigger and its panel cannot drift apart, and so adding a tab is one row.
 */
const CUSTOMER_APP_TABS: readonly {
  value: string;
  Icon: LucideIcon;
  labelKey: string;
  Panel: ComponentType<TenantTabPanelProps>;
}[] = [
  {
    value: 'settings',
    Icon: Settings,
    labelKey: 'adminSettings.tenant.tabSettings',
    Panel: TenantSettingsTab
  },
  { value: 'apps', Icon: Puzzle, labelKey: 'adminSettings.tenant.tabApps', Panel: TenantAppsTab },
  {
    value: 'usage',
    Icon: Gauge,
    labelKey: 'adminSettings.tenant.tabUsage',
    Panel: TenantAiUsageTab
  }
];

export function TenantDetailPage() {
  const { tenantId } = useParams({ from: '/admin/tenants/$tenantId/' });
  const { t } = useTranslation();
  const { data: tenantsData } = useListTenants();
  const { data: users, isLoading: usersLoading } = useListTenantUsers(tenantId);
  const sendInvitation = useSendTenantUserInvitation(tenantId);
  const generateActivationLink = useGenerateTenantUserActivationLink(tenantId);
  const deleteUser = useDeleteTenantUser(tenantId);
  const pendingUserIds = usePendingTenantUserIds(tenantId);

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<TenantUserAdminListItem | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | undefined>(undefined);
  const [resettingSignInUser, setResettingSignInUser] = useState<TenantUserAdminListItem>();

  const tenant = tenantsData?.tenants.find((tn) => tn._id === tenantId);
  // A partner tenant runs the partner area only: no customer app to entitle,
  // no AI features to switch on and therefore no AI spend to report. Showing
  // those tabs invites an operator to configure a surface the tenant will
  // never reach. Withheld until the tenant is known, so a partner tenant never
  // paints the customer tabs and then drops them.
  const showsCustomerAppTabs = tenant !== undefined && runsCustomerApp(tenant.tenantType);

  useDocumentTitle(tenant ? `${t('tenantAdmin.title')}: ${tenant.name}` : t('tenantAdmin.title'));

  const handleAddUser = () => {
    setEditingUser(undefined);
    setAddUserOpen(true);
  };

  const handleEdit = useCallback((user: TenantUserAdminListItem) => {
    setEditingUser(user);
    setAddUserOpen(true);
  }, []);

  const handleDeleteClick = useCallback((user: TenantUserAdminListItem) => {
    setDeletingUserId(user._id);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deletingUserId) return;

    try {
      await deleteUser.mutateAsync(deletingUserId);
      toast.success(t('adminSettings.tenant.deleteUserSuccess'));
      setDeleteDialogOpen(false);
      setDeletingUserId(undefined);
    } catch (error) {
      toast.error(t('adminSettings.tenant.deleteUserFailed'));
      console.error('Failed to delete user:', error);
    }
  };

  const handleSendInvitation = useCallback(
    async (userId: string) => {
      try {
        await sendInvitation.mutateAsync({ userId });
        toast.success(t('adminSettings.tenant.sendInvitationSuccess'));
      } catch (error) {
        toastApiError(error, 'adminSettings.tenant.sendInvitationFailed');
        console.error('Failed to send invitation:', error);
      }
    },
    [sendInvitation, t]
  );

  const handleCopyActivationLink = useCallback(
    async (userId: string) => {
      let activationLink: string;
      try {
        const { data } = await generateActivationLink.mutateAsync({ userId });
        activationLink = data.activationLink;
      } catch (error) {
        toastApiError(error, 'adminSettings.tenant.copyActivationLinkFailed');
        console.error('Failed to generate activation link:', error);
        return;
      }

      // The link is already minted at this point. If the clipboard API is
      // unavailable (e.g. an insecure, non-localhost context where
      // navigator.clipboard is undefined), surface the link directly so the
      // admin can still copy it manually instead of being stranded.
      try {
        await navigator.clipboard.writeText(activationLink);
        toast.success(t('adminSettings.tenant.copyActivationLinkSuccess'));
      } catch (error) {
        toast.message(t('adminSettings.tenant.copyActivationLinkManual'), {
          description: activationLink,
          duration: Infinity
        });
        console.error('Failed to write activation link to clipboard:', error);
      }
    },
    [generateActivationLink, t]
  );

  // Define table columns with stable reference using useMemo
  const columns = useMemo<DataTableColumnDef<TenantUserAdminListItem>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        header: createColumnHeader(t('adminSettings.tenant.columnName')),
        meta: { label: t('adminSettings.tenant.columnName'), filterType: 'text' },
        enableColumnFilter: true,
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.firstName} {row.original.lastName}
          </span>
        )
      },
      {
        accessorKey: 'email',
        header: createColumnHeader(t('adminSettings.tenant.columnEmail')),
        meta: { label: t('adminSettings.tenant.columnEmail'), filterType: 'text' },
        enableColumnFilter: true,
        cell: ({ row }) => <span className="text-muted-foreground">{row.getValue('email')}</span>
      },
      {
        accessorKey: 'userTypes',
        header: createColumnHeader(t('adminSettings.tenant.columnUserTypes')),
        meta: {
          label: t('adminSettings.tenant.columnUserTypes'),
          filterType: 'select',
          filterOptions: tenant ? getUserTypeOptions(t, tenant.tenantType) : []
        },
        filterFn: filterArrayIncludes,
        enableColumnFilter: true,
        cell: ({ row }) => {
          const types = row.getValue('userTypes') as UserType[];
          return (
            <div className="flex flex-wrap gap-1">
              {types.map((type) => (
                <Badge key={type} variant="outline">
                  {t(`userTypes.${type}`)}
                </Badge>
              ))}
            </div>
          );
        },
        enableGlobalFilter: false
      },
      {
        accessorKey: 'status',
        header: createColumnHeader(t('adminSettings.tenant.columnStatus')),
        meta: {
          label: t('adminSettings.tenant.columnStatus'),
          filterType: 'select',
          filterOptions: getUserStatusOptions(t)
        },
        enableColumnFilter: true,
        cell: ({ row }) => {
          const status = row.getValue('status') as UserStatus;
          return (
            <div className="flex flex-wrap items-center gap-1">
              <Badge colorScheme={getUserStatusColorScheme(status)}>
                {getUserStatusLabel(status, t)}
              </Badge>
              {row.original.homeOrgDeleted && (
                <HomeOrgDeletedBadge
                  label={t('tenantAdmin.homeOrgDeletedBadge')}
                  tooltip={t('tenantAdmin.homeOrgDeletedTooltip')}
                />
              )}
            </div>
          );
        },
        enableGlobalFilter: false
      },
      {
        accessorKey: 'classification',
        header: createColumnHeader(t('adminSettings.tenant.columnClassification')),
        meta: {
          label: t('adminSettings.tenant.columnClassification'),
          filterType: 'select',
          filterOptions: getUserClassificationOptions(t)
        },
        enableColumnFilter: true,
        cell: ({ row }) => {
          const user = row.original;
          return (
            <Badge colorScheme={user.classification === 'internal' ? 'success' : 'neutral'}>
              {getUserClassificationLabel(user.classification, t)}
              {user.externalOrganization && ` (${user.externalOrganization})`}
            </Badge>
          );
        },
        enableGlobalFilter: false
      },
      createActionsColumn<TenantUserAdminListItem>({
        isRowPending: (user) => pendingUserIds.has(user._id),
        onEdit: handleEdit,
        onDelete: handleDeleteClick,
        getCustomActions: (user) =>
          getTenantUserStatusActions(
            user,
            {
              onSendInvitation: handleSendInvitation,
              onCopyActivationLink: handleCopyActivationLink,
              onResetSignIn: setResettingSignInUser
            },
            t
          )
      })
    ],
    [
      handleEdit,
      handleDeleteClick,
      handleSendInvitation,
      handleCopyActivationLink,
      pendingUserIds,
      t,
      tenant
    ]
  );

  return (
    <Page>
      <PageContent>
        {/* Header */}
        <div className="mb-6">
          <div className="mb-4">
            <BackButton to="/admin/tenants" />
          </div>

          {tenant && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold tracking-tight">{tenant.name}</h1>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>{t('adminSettings.tenant.slug', { slug: tenant.slug })}</span>
                <TenantTypeBadge tenantType={tenant.tenantType} />
                <Badge colorScheme={tenant.active ? 'success' : 'error'}>
                  {tenant.active ? t('tenantAdmin.statusActive') : t('tenantAdmin.statusInactive')}
                </Badge>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users">
          <TabsList>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              {t('adminSettings.tenant.tabUsers')}
            </TabsTrigger>
            {showsCustomerAppTabs &&
              CUSTOMER_APP_TABS.map(({ value, Icon, labelKey }) => (
                <TabsTrigger key={value} value={value}>
                  <Icon className="w-4 h-4 mr-2" />
                  {t(labelKey)}
                </TabsTrigger>
              ))}
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{t('adminSettings.tenant.tabUsers')}</h2>
              <Button onClick={handleAddUser}>
                <Plus className="w-4 h-4 mr-2" />
                {t('adminSettings.tenant.addUser')}
              </Button>
            </div>

            <DataTable
              columns={columns}
              data={users || []}
              isLoading={usersLoading}
              enableGlobalFilter
              enableColumnFilters
              enableUrlState
              tableId="tenantUsers"
            />
          </TabsContent>

          {showsCustomerAppTabs &&
            CUSTOMER_APP_TABS.map(({ value, Panel }) => (
              <TabsContent key={value} value={value} className="mt-6">
                <Panel tenantId={tenantId} tenant={tenant} />
              </TabsContent>
            ))}
        </Tabs>

        {/* Add/Edit User Dialog */}
        <AddTenantUserDialog
          open={addUserOpen}
          onOpenChange={setAddUserOpen}
          tenantId={tenantId}
          editingUser={editingUser}
        />

        <ResetTenantUserSignInDialog
          tenantId={tenantId}
          user={resettingSignInUser}
          onClose={() => setResettingSignInUser(undefined)}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDeleteConfirm}
          isDeleting={deleteUser.isPending}
          title={t('adminSettings.tenant.deleteUserTitle')}
          description={t('adminSettings.tenant.deleteUserDescription')}
        />
      </PageContent>
    </Page>
  );
}
